<template>
	<div class="relative flex w-full flex-col" :style="{ height }">
		<!-- Map Controls (top right) -->
		<MainMapControls
			v-model:style-selection="styleSelection"
			@center-map="centerMapOnUserLocation"
		/>

		<div ref="mapContainerRef" class="h-full w-full" />

		<!-- Loading Overlay -->
		<div
			v-if="isLoading"
			class="absolute inset-0 z-10 flex items-center justify-center bg-gray-100/80"
		>
			<div class="flex flex-col items-center">
				<GenericLoader message="Loading map..." class="mb-2" />
			</div>
		</div>

		<!-- Slot for custom controls/overlays -->
		<slot name="controls" :map="map" :is-initialized="isInitialized" />
	</div>
</template>

<script setup lang="ts">
	import { ref, onMounted, computed, PropType, Ref, watch } from "vue";
	import { PlantingDTO } from "seedgreen-shared/dtos/PlantingDTO";
	import usePlantingsLayer from "@/composables/map/usePlantingsLayer";
	import GenericLoader from "@/components/ui/GenericLoader.vue";
	import { useBaseMap } from "@/components/ui/maps/useBaseMap";
	import MainMapControls from "@/views/MainMap/MainMapControls.vue";
	import { useMapStyles } from "@/composables/map/useMapStyles";

	const { getPreferredMapStyle } = useMapStyles(ref(null));
	const initialStyle = getPreferredMapStyle();
	const styleSelection = ref<string>(initialStyle);

	const props = defineProps({
		height: {
			type: String,
			default: "400px",
		},
		initialLocation: {
			type: Array as unknown as PropType<[number, number] | null>,
			default: null,
		},
		plantings: {
			type: Array as PropType<PlantingDTO[] | null>,
			default: () => null,
		},
	});

	const mapContainerRef = ref<HTMLElement | null>(null);

	// Initialize base map with preferred style
	const { map, isInitialized, isLoading, initializeMap } = useBaseMap(
		mapContainerRef,
		{
			initialCenter: props.initialLocation || undefined,
			style: initialStyle
				? `mapbox://styles/mapbox/${initialStyle}`
				: undefined,
		},
	);

	// Map style switching
	const {
		setMapStyle: setMapboxStyle,
		getPreferredMapStyle: getPreferredMapboxStyle,
	} = useMapStyles(map as Ref<mapboxgl.Map | null>);

	// Keep styleSelection in sync with localStorage and map style
	watch(styleSelection, (newStyle) => {
		if (newStyle !== getPreferredMapboxStyle()) {
			setMapboxStyle(newStyle);
		}
	});

	// When the map style changes (e.g., programmatically or after reload), update styleSelection
	function handleStyleChange() {
		const current = getPreferredMapboxStyle();
		if (styleSelection.value !== current) {
			styleSelection.value = current;
		}
	}

	// Center map on user location
	function centerMapOnUserLocation() {
		if (!map.value) return;
		navigator.geolocation.getCurrentPosition((pos) => {
			map.value?.flyTo({
				center: [pos.coords.longitude, pos.coords.latitude],
				zoom: 14,
			});
		});
	}

	// Initialize plantings layer
	const safePlantings = computed(() => props.plantings ?? []);
	const { initPlantingsSources, initPlantingsLayers, centerMapOnPlantings } =
		usePlantingsLayer(
			map as Ref<mapboxgl.Map | null>,
			safePlantings,
			() => null,
		);

	// Helper to (re)initialize plantings if needed
	function reinitPlantingsIfNeeded() {
		if (map.value && safePlantings.value.length > 0) {
			initPlantingsSources();
			initPlantingsLayers();
			if (!props.initialLocation) {
				centerMapOnPlantings();
			}
		}
	}

	// Initialize map and setup layers
	onMounted(async () => {
		await initializeMap();

		// Listen for style.load to re-add plantings layers after style change
		watch(
			() => map.value,
			(newMap) => {
				if (!newMap) return;
				const handler = () => {
					reinitPlantingsIfNeeded();
					handleStyleChange(); // keep styleSelection in sync
				};
				newMap.on("style.load", handler);
			},
			{ immediate: true },
		);
	});

	watch(
		() => safePlantings.value.map((p) => p.id).join(","),
		() => {
			if (
				map.value &&
				isInitialized.value &&
				safePlantings.value.length > 0
			) {
				initPlantingsSources();
				initPlantingsLayers();
				if (!props.initialLocation) {
					centerMapOnPlantings();
				}
			}
		},
		{ immediate: true },
	);

	watch(
		() => isInitialized.value,
		(newVal) => {
			if (newVal && map.value && safePlantings.value.length > 0) {
				initPlantingsSources();
				initPlantingsLayers();
				if (!props.initialLocation) {
					centerMapOnPlantings();
				}
			}
		},
		{ immediate: true },
	);

	defineExpose({
		map,
		isInitialized,
		isLoading,
	});
</script>
